import { useEffect, useState, useCallback } from "react";

/**
 * Rate limit client-side para tentativas de auth.
 * Após MAX_ATTEMPTS falhas, bloqueia por COOLDOWN_MS.
 *
 * Não substitui rate limit de servidor — o Supabase já faz isso por IP.
 * Apenas reduz bots triviais que disparam o form em loop.
 */

const MAX_ATTEMPTS = 5;
const COOLDOWN_MS = 60 * 1000;
const STORAGE_PREFIX = "auth_attempts:";

export type AuthAction = "login" | "signup" | "reset";

interface AttemptRecord {
  count: number;
  blockedUntil: number; // epoch ms; 0 = não bloqueado
}

function storageKey(action: AuthAction, identifier: string): string {
  const id = (identifier || "anon").trim().toLowerCase();
  return `${STORAGE_PREFIX}${action}:${id}`;
}

function readRecord(action: AuthAction, identifier: string): AttemptRecord {
  if (typeof window === "undefined") return { count: 0, blockedUntil: 0 };
  try {
    const raw = window.localStorage.getItem(storageKey(action, identifier));
    if (!raw) return { count: 0, blockedUntil: 0 };
    const parsed = JSON.parse(raw) as AttemptRecord;
    return {
      count: Number(parsed.count) || 0,
      blockedUntil: Number(parsed.blockedUntil) || 0,
    };
  } catch {
    return { count: 0, blockedUntil: 0 };
  }
}

function writeRecord(action: AuthAction, identifier: string, record: AttemptRecord) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(action, identifier), JSON.stringify(record));
  } catch {
    // localStorage cheio/bloqueado — ignora
  }
}

export function recordFailedAttempt(action: AuthAction, identifier: string): AttemptRecord {
  const current = readRecord(action, identifier);
  const nextCount = current.count + 1;
  const next: AttemptRecord = {
    count: nextCount,
    blockedUntil: nextCount >= MAX_ATTEMPTS ? Date.now() + COOLDOWN_MS : 0,
  };
  writeRecord(action, identifier, next);
  return next;
}

export function resetAttempts(action: AuthAction, identifier: string) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(storageKey(action, identifier));
  } catch {
    // ignora
  }
}

export function getBlockedRemainingSeconds(action: AuthAction, identifier: string): number {
  const rec = readRecord(action, identifier);
  if (!rec.blockedUntil) return 0;
  const remaining = Math.ceil((rec.blockedUntil - Date.now()) / 1000);
  return remaining > 0 ? remaining : 0;
}

/**
 * Hook que devolve o cooldown atual em segundos para uma ação + identifier.
 * Atualiza a cada segundo enquanto > 0.
 */
export function useAuthCooldown(action: AuthAction, identifier: string): number {
  const compute = useCallback(
    () => getBlockedRemainingSeconds(action, identifier),
    [action, identifier],
  );
  const [remaining, setRemaining] = useState<number>(compute);

  useEffect(() => {
    setRemaining(compute());
    const interval = setInterval(() => {
      const next = compute();
      setRemaining(next);
      if (next <= 0) {
        // limpa o bloqueio expirado para liberar próximas tentativas com contador zerado
        const rec = readRecord(action, identifier);
        if (rec.blockedUntil && rec.blockedUntil <= Date.now()) {
          resetAttempts(action, identifier);
        }
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [action, identifier, compute]);

  return remaining;
}

export const AUTH_LIMITER_MAX_ATTEMPTS = MAX_ATTEMPTS;
